import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './EventSpotlight.module.css';
import { CountdownTimer, TimetableDisplay, RegisteredUsersDisplay } from './components';
import apiClient from '../../../services/apiClient';

interface Event {
  Event_ID: string;
  title: string;
  sub_title?: string;
  event_date: string;
  register_start_date: string;
  register_end_date: string;
  vote_end_date: string;
  min_select_users: number;
  max_select_users: number;
  round: number;
  status: number;
  notification_enabled: boolean;
  postal_code?: string;
  street?: string;
  house_number?: string;
  city?: string;
  country?: string;
  location_name?: string;
  Branding?: string;
  Verification_Code: string[];
}

interface User {
  User_ID: string;
  firstname: string;
  lastname: string;
  title?: string;
  mail: string;
  jobtitle?: string;
  picture?: string;
  profile?: string;
  linkedin?: string;
  is_registered?: boolean;
  firm?: {
    Firm_ID: string;
    abbrev: string;
    name?: string;
    logo?: string;
  };
}

interface Registration {
  Registration_ID: string;
  User_ID: string;
  Event_ID: string;
  created_at: string;
}

interface TimetableEntry {
  startTime: string;
  duration: string;
  type: 'round' | 'break' | 'welcome' | 'closing' | 'other';
  description?: string;
}

interface Timetable {
  entries: TimetableEntry[];
}

interface Group {
  group_id: string;
  group_members: string[];
  group_size: number;
  created_at?: string;
}

interface GroupData {
  round_number: number;
  groups: Group[];
}

interface SpotlightData {
  event: Event;
  timetable: Timetable;
  registration: Registration[];
  users: User[];
  best_matches: GroupData[];
}

const EventSpotlight: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SpotlightData | null>(null);

  const eventId = searchParams.get('event');
  const verificationCode = searchParams.get('verification_code');

  useEffect(() => {
    if (!eventId) {
      toast.error('Event ID is required.', {
        position: 'top-right',
        autoClose: 5000,
      });
      navigate('/');
      return;
    }

    if (!verificationCode) {
      toast.error('Verification code is required.', {
        position: 'top-right',
        autoClose: 5000,
      });
      navigate('/');
      return;
    }

    fetchSpotlightData();
  }, [eventId, verificationCode, navigate]);

  const fetchSpotlightData = async () => {
    try {
      setLoading(true);
      
      const response = await apiClient.get(
        `/api/v2/web/events/spotlight?event=${eventId}&verification_code=${verificationCode}`
      );

      setData(response.data);
    } catch (error: any) {
      console.error('Error fetching spotlight data:', error);
      
      let errorMessage = 'An error occurred while loading the event data';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.detail) {
        errorMessage = error.detail;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
      });
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const getUserById = (userId: string): User | null => {
    return data?.users.find(user => user.User_ID === userId) || null;
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="row justify-content-center align-items-center min-vh-100">
          <div className="col-12 text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-3 text-muted">Loading event data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mt-4">
        <div className="row justify-content-center align-items-center min-vh-100">
          <div className="col-12 text-center">
            <div className="alert alert-danger">
              <h4 className="alert-heading">Event not found</h4>
              <p>The requested event could not be loaded.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        <div className="row">
          <div className="col-12">
            {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="mb-4">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <span>Event Spotlight</span>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {data.event.title}
                </li>
              </ol>
            </nav>

            {/* Event Header */}
            <div className={styles.eventHeader}>
              <div className="row mb-4">
                <div className="col-12">
                  <div className={styles.eventHeaderCard}>
                    <div className={styles.eventHeaderContent}>
                      <div className={styles.eventIcon}>
                        <i className="bi bi-calendar-event"></i>
                      </div>
                      <div className={styles.eventInfo}>
                        <h1 className={styles.eventTitle}>
                          {data.event.title}
                        </h1>
                        {data.event.sub_title && (
                          <p className={styles.eventSubtitle}>{data.event.sub_title}</p>
                        )}
                      </div>
                    </div>
                    <div className={styles.eventCountdownSection}>
                      <CountdownTimer event={data.event} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Timetable Section */}
            <div className="row mb-5">
              <div className="col-12">
                <TimetableDisplay timetable={data.timetable} />
              </div>
            </div>

            {/* Match Rounds Section */}
            <div className="row mb-5">
              <div className="col-12">
                <div className={styles.viewContainer}>
                  <div className={styles.viewHeader}>
                    <h3 className={styles.viewTitle}>Match Rounds</h3>
                  </div>

                  <div className={styles.roundsContainer}>
                    {data.best_matches && data.best_matches.length > 0 ? (
                      data.best_matches.map((roundData) => (
                        <div key={roundData.round_number} className={`card p-4 role-container shadow-sm mb-4 ${styles.roundContainer}`}>
                          <h2 className={styles.roundTitle}>Round {roundData.round_number}</h2>
                          
                          {roundData.groups.map((group, groupIndex) => (
                            <div key={`${roundData.round_number}-${groupIndex}`} className={styles.group}>
                              <div className={styles.groupHeader}>
                                <h5 className={styles.groupTitle}>Group {groupIndex + 1}</h5>
                                <span className={styles.groupSize}>{group.group_size} members</span>
                              </div>
                              
                              <div className={styles.groupMembers}>
                                {group.group_members.map((memberId, memberIndex) => {
                                  const user = getUserById(memberId);
                                  
                                  if (!user) return null;

                                  return (
                                    <div key={`${group.group_id}-${memberIndex}`} className={styles.member}>
                                      <div className="d-flex align-items-center">
                                        {user.picture ? (
                                          <img 
                                            src={`data:image/png;base64,${user.picture}`} 
                                            className={styles.userImg} 
                                            alt={`${user.firstname} ${user.lastname}`}
                                          />
                                        ) : (
                                          <img 
                                            src="/static/images/user-profile-icon.png" 
                                            className={styles.userImg} 
                                            alt={`${user.firstname} ${user.lastname}`}
                                          />
                                        )}
                                        <div>
                                          <div className={styles.userName}>
                                            {user.firstname} {user.lastname}
                                            {!user.is_registered && (
                                              <span className="text-muted ms-1">(Unregistered)</span>
                                            )}
                                          </div>
                                          {user.title && (
                                            <div className={styles.userTitle}>
                                              {user.title}
                                            </div>
                                          )}
                                          {user.jobtitle && (
                                            <div className={styles.userJobTitle}>
                                              {user.jobtitle}
                                            </div>
                                          )}
                                          {user.firm && (
                                            <div className={styles.userFirm}>
                                              {user.firm.name || user.firm.abbrev}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              
                              {group.created_at && (
                                <div className={styles.groupTimestamp}>
                                  Group created: {new Date(group.created_at).toLocaleString()}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ))
                    ) : (
                      <div className={styles.emptyState}>
                        <div className="text-center py-5">
                          <i className="bi bi-calendar-x text-muted" style={{ fontSize: '3rem' }}></i>
                          <h5 className="text-muted mt-3">No matches available yet</h5>
                          <p className="text-muted">Matches will appear here once they have been created.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Registered Users Section */}
            <div className="row mb-5">
              <div className="col-12">
                <RegisteredUsersDisplay 
                  registration={data.registration}
                  users={data.users}
                />
              </div>
            </div>

            {/* Event Location Information */}
            {(data.event.location_name || data.event.street) && (
              <div className="row mt-5">
                <div className="col-12">
                  <div className={styles.locationCard}>
                    <div className={styles.locationHeader}>
                      <h3 className={styles.locationTitle}>
                        <i className="bi bi-geo-alt-fill me-2"></i>
                        Event Location
                      </h3>
                    </div>
                    <div className={styles.locationContent}>
                      <div className="row">
                        <div className="col-md-6">
                          <div className={styles.locationInfo}>
                            {data.event.location_name && (
                              <div className={styles.locationName}>
                                <i className="bi bi-building me-2"></i>
                                {data.event.location_name}
                              </div>
                            )}
                            {data.event.street && (
                              <div className={styles.locationAddress}>
                                <i className="bi bi-pin-map me-2"></i>
                                <div>
                                  {data.event.street} {data.event.house_number}<br />
                                  {data.event.postal_code} {data.event.city}<br />
                                  {data.event.country}
                                </div>
                              </div>
                            )}
                            {data.event.street && (
                              <div className={styles.locationActions}>
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                    `${data.event.street} ${data.event.house_number}, ${data.event.postal_code} ${data.event.city}, ${data.event.country}`
                                  )}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.mapButton}
                                >
                                  <i className="bi bi-map me-2"></i>
                                  View on Google Maps
                                </a>
                                <a
                                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                                    `${data.event.street} ${data.event.house_number}, ${data.event.postal_code} ${data.event.city}, ${data.event.country}`
                                  )}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.directionsButton}
                                >
                                  <i className="bi bi-signpost-2 me-2"></i>
                                  Get Directions
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                        {data.event.street && (
                          <div className="col-md-6">
                            <div className={styles.mapContainer}>
                              <iframe
                                src={`https://maps.google.com/maps?q=${encodeURIComponent(
                                  `${data.event.street} ${data.event.house_number}, ${data.event.postal_code} ${data.event.city}, ${data.event.country}`
                                )}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                                width="100%"
                                height="200"
                                style={{ border: 0, borderRadius: '8px' }}
                                allowFullScreen={true}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Event Location Map"
                              ></iframe>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventSpotlight;
